import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Rss } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useContentLibrary } from "@/data/content";
import { API_BASE_URL } from "@/config/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function BlogIndex() {
  const { data, loading } = useContentLibrary();
  const [featured, ...rest] = data.blogPosts;

  return (
    <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <SEO
        title="Field Notes"
        description="Essays and field notes from the Jaisphere team — AI governance, regulated cloud, agentic discovery, and the engineering inside production-grade modernization."
        path="/blog"
      />
      <Nav />

      <section className="pt-32 md:pt-40 pb-12">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
            Field Notes
          </span>
          <h1 className="mt-4 font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-zinc-950 dark:text-zinc-50">
            Practical writing
            <br /> for serious teams.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Essays and field notes from the Jaisphere team — AI governance, regulated cloud, and the engineering inside both.
          </p>
          <div className="mt-7">
            <a
              href={`${API_BASE_URL}/blog/rss.xml`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rss-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-[#002FA7] dark:text-zinc-300 dark:hover:text-[#7C9BFF] border border-zinc-200 dark:border-zinc-800 px-3 py-2 rounded-sm"
            >
              <Rss size={14} className="text-[#F26522]" />
              Subscribe via RSS
            </a>
          </div>
        </div>
      </section>

      {loading && (
        <section className="pb-24 md:pb-32">
          <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 text-zinc-600 dark:text-zinc-400">Loading posts...</div>
        </section>
      )}

      {featured && (
        <section className="pb-12">
          <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-card-${featured.slug}`}
              className="group block border border-zinc-200 hover:border-zinc-950 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(9,9,11,0.18)] dark:border-zinc-800 dark:hover:border-zinc-50"
            >
              <div className="grid grid-cols-12">
                <div className="col-span-12 md:col-span-6 bg-zinc-100 dark:bg-zinc-900 relative h-64 md:h-auto">
                  <img src={featured.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="col-span-12 md:col-span-6 p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                    <span className="px-2 py-1 border border-zinc-200 dark:border-zinc-800">{featured.category}</span>
                    <span>{fmtDate(featured.date)} · {featured.readTime}</span>
                  </div>
                  <h2 className="mt-5 font-display font-bold tracking-tight text-2xl sm:text-3xl text-zinc-950 dark:text-zinc-50 group-hover:text-[#002FA7] dark:group-hover:text-[#7C9BFF] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-3 text-zinc-600 dark:text-zinc-400 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] dark:text-[#7C9BFF]">
                    Read essay <ArrowUpRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="pb-24 md:pb-32">
          <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {rest.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block border border-zinc-200 hover:border-zinc-950 rounded-sm overflow-hidden h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(9,9,11,0.18)] dark:border-zinc-800 dark:hover:border-zinc-50"
                >
                  <div className="h-48 bg-zinc-100 dark:bg-zinc-900 relative">
                    <img src={p.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                      <span className="px-2 py-1 border border-zinc-200 dark:border-zinc-800">{p.category}</span>
                      <span>{p.readTime}</span>
                    </div>
                    <h3 className="mt-4 font-display font-bold tracking-tight text-xl text-zinc-950 dark:text-zinc-50 group-hover:text-[#002FA7] dark:group-hover:text-[#7C9BFF] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-5 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                      {fmtDate(p.date)} · {p.author}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
